import { useState, useEffect, cloneElement } from "react";
import styled from "styled-components";
import ReactSwal from "../utils/swalUtils";
import { Formik, Form } from "formik";
import { PropTypes } from "prop-types";
import { useMutation, useQueryCache } from "react-query";
import { useCookies } from "react-cookie";
import InputDiv from "./InputDiv";
import FileUploadInput from "./FileUploadInput";
import Loader from "./Loader";
import SelectField from "./SelectField";
import Button from "./Button";
import * as Yup from "yup";
import { useDiscoverySettings } from "../api/queries";
import { addDish, editDish } from "../api/mutations";
import find from "lodash/find";
import forEach from "lodash/forEach";
import { handleUnknown, handle500 } from "../utils/errorHandles";
import { trackEventSegment } from "../utils/segmentUtils";
import ENV from "../constants/envConstants";
import { ThemeProvider } from "styled-components";
import breakpoints from "../constants/breakpoints";
import { useMediaQuery } from "react-responsive";
import dishExample from "../assets/images/dish-example.jpg";

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const FormWrap = styled.div`
  padding: 2em;
  border: #ff4256 2px solid;
  border-radius: 0.5rem;
`;

const FormHeading = styled.span`
  font-weight: 500;
  font-size: 1.5em;
  margin-bottom: 0.6em;
  display: block;
`;

const FormSubheading = styled.span`
  font-weight: 500;
  font-size: 0.9em;
  display: block;
  margin-bottom: 2em;
`;


const MenuItemForm = (props) => {
  const [cookies] = useCookies(["placeId"]);

  const cache = useQueryCache();

  const initialValues = {
    name: props.dish ? props.dish.name : "",
    description: props.dish ? props.dish.description : "",
    price: props.dish ? props.dish.price : "",
    image: undefined,
  };

  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [selectedDietaryOptions, setSelectedDietaryOptions] = useState([]);

  const [mutate] = useMutation(props.dish ? editDish : addDish);

  const { data } = useDiscoverySettings();

  useEffect(() => {
    if (loaded === false && data) {
      var dietaryPrefs = [];
      data.map((item) => {
        dietaryPrefs.push(item.name);
      });
      setDietaryOptions(dietaryPrefs);
      setLoaded(true);
    }
  }, [loaded, data]);

  const correctImageUrl = () => {
    if (props.dish.image.includes("http")) {
      return props.dish.image;
    }
    return ENV.BACKEND_BASE_URL + props.dish.image;
  };

  const [imagePreview, setImagePreview] = useState(
    props.dish && props.dish.image ? correctImageUrl() : null
  );

  const setImagePrev = (url) => {
    setImagePreview(url);
  };

  useEffect(() => {
    if (props.dish) {
      forEach(props.dish, (value, field) => {
        if (field !== "image") {
          initialValues[field] = value;
        }
      });
      handleSelectedChoices(props.dish.discovery_settings);
      if (props.dish.image && props.hasImageField) {
        setImagePrev(correctImageUrl());
      }
    }
  }, [props.dish]);

  const handleSelectedChoices = (newChoices) => {
    setSelectedDietaryOptions(newChoices);
  };

  const getFileObjs = async (values) => {
    return new Promise((res, rej) => {
      if (values.image) {
        let reader = new FileReader();
        reader.onloadend = () => {
          return res(reader.result);
        };
        reader.readAsDataURL(values.image);
      }
    });
  };

  const dishCreate = async (valuesToSend, bag) => {
    await mutate(
      {
        values: {
          dishes: [
            {
              dish: valuesToSend,
              dish_order: props.numOfDishes + 1,
            },
          ],
        },
        categoryId: props.sectionId,
      },
      {
        onSuccess: (resData) => {
          const resDishes = resData.data.dishes;
          const dish = find(resDishes, ["dish_order", props.numOfDishes + 1]);
          trackEventSegment("Added Dish", {
            name: dish.dish.name,
            uuid: dish.dish.uuid,
          });
          bag.resetForm();
          setImagePrev(null);
          setSelectedDietaryOptions([]);
          ReactSwal.fire({
            title: "Dish created",
            icon: "success",
            timer: 2000,
          });
        },
        onSettled: () => {
          cache.invalidateQueries("placeDetail");
        },
        onError: (error) => {
          const status = error.response.status;
          const resData = error.response.data;
          switch (status) {
            case 400:
              if (resData.error_type === "ValidationError") {
                forEach(resData.errors[0].message.dish, (message, field) => {
                  bag.setFieldError(field, message[0]);
                });
              } else {
                handleUnknown();
              }
              break;
            case 500:
              handle500();
              break;
            default:
              handleUnknown();
              break;
          }
        },
      }
    );
    bag.setSubmitting(false);
    setSubmitting(false);
  };

  const dishEdit = async (valuesToSend, bag) => {
    await mutate(
      {
        values: valuesToSend,
        dishId: props.dish.uuid,
      },
      {
        onSuccess: (resData) => {
          const editedDish = resData.data;
          trackEventSegment("Edited Dish", {
            name: editedDish.name,
            uuid: editedDish.uuid,
          });
          bag.resetForm();
          setImagePrev(null);
          setSelectedDietaryOptions([]);
          ReactSwal.fire({
            title: "Dish updated",
            icon: "success",
          });
        },
        onSettled: () => {
          cache.invalidateQueries("placeDetail");
        },
        onError: (error) => {
          const status = error.response.status;
          const resData = error.response.data;
          switch (status) {
            case 400:
              if (resData.error_type === "ValidationError") {
                forEach(resData.errors, (error) => {
                  bag.setFieldError(error["field"], error["message"]);
                });
              } else {
                handleUnknown();
              }
              break;
            case 500:
              handle500();
              break;
            default:
              handleUnknown();
              break;
          }
        },
      }
    );
    bag.setSubmitting(false);
    setSubmitting(false);
  };

  const onSubmit = (values, bag) => {
    const valuesToSend = Object.assign({}, values);
    valuesToSend["price"] = valuesToSend["price"].replace("$", "");
    valuesToSend["discovery_settings"] = selectedDietaryOptions;
    if (values.image && props.hasImageField) {
      getFileObjs(values).then((fileObj) => {
        valuesToSend["image"] = fileObj;
        props.dish ? dishEdit(valuesToSend, bag) : dishCreate(valuesToSend, bag);
      });
    } else {
      props.dish ? dishEdit(valuesToSend, bag) : dishCreate(valuesToSend, bag);
    }
  };

  const schema = Yup.object({
    name: Yup.string().required("This field is required."),
    price: Yup.string().required("This field is required."),
    image: Yup.mixed()
      .notRequired()
      .test({
        name: "fileFormat",
        test: function test(value) {
          if (!!value) {
            if (!ALLOWED_FILE_TYPES.includes(value.type)) {
              return this.createError({
                path: "image",
                message: "File format '" + value.type + "' is not supported.",
              });
            }
          }
          return true;
        },
      }),
  });

  const formContent = () => {
    return (
      <ThemeProvider theme={breakpoints}>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={schema}
        >
          {(formik) =>
            formik.isSubmitting || submitting ? (
              <Loader />
            ) : (
              <FormWrap>
                <FormHeading>
                  {props.dish ? "Edit" : "Add new"} menu item
                </FormHeading>
                <FormSubheading>
                  You can drag and drop the items to change their order, but
                  only after you’re finished editing.
                </FormSubheading>
                <Form>
                  <InputDiv
                    text="Menu item name"
                    placeholder="For example avocado toast"
                    name="name"
                    hasLabel={true}
                  />
                  <InputDiv
                    text="Description (optional)"
                    placeholder="For example sourdough with avocado, feta and poached eggs"
                    name="description"
                    hasLabel={true}
                    type="textarea"
                  />
                  <InputDiv
                    text="Price"
                    placeholder="For example $12"
                    name="price"
                    hasLabel={true}
                  />
                  {props.hasImageField && (
                    <FileUploadInput
                      name="image"
                      text="Item photo (optional)"
                      exampleFile={dishExample}
                      helpText={
                        "This image will be used as the items image in your visual menu. Recommended image dimensions 350x500px, to see an example of an ideal image"
                      }
                      hasLabel={true}
                      handleFileInput={(fileName, fieldName) => {
                        formik.setFieldValue(fieldName, fileName);
                      }}
                      removePreview={() => null}
                      setPreview={setImagePrev}
                      preview={imagePreview}
                      previewNewTab={true}
                    />
                  )}
                  {loaded ? (
                    <>
                      <SelectField
                        choices={dietaryOptions}
                        selectedChoices={selectedDietaryOptions}
                        handleSelectedChoices={handleSelectedChoices}
                        label="Dietary requirements (optional)"
                      />
                      <Button
                        text={props.dish ? "Edit menu item" : "Add menu item"}
                        hasMargin={false}
                        type="submit"
                        submitting={formik.isSubmitting}
                      />
                    </>
                  ) : (
                    <Loader />
                  )}
                </Form>
              </FormWrap>
            )
          }
        </Formik>
      </ThemeProvider>
    );
  };

  const isLargerScreen = useMediaQuery({
    query: "(max-width: 850px)",
  });

  const newDish = () => {
    ReactSwal.fire({
      html: formContent(),
      width: isLargerScreen ? "90%" : "50%",
      padding: 0,
      showConfirmButton: false,
      customClass: {
        content: "swal-additem",
      },
      willClose: () => {
        setSelectedDietaryOptions([]);
      },
    });
  };

  const El = cloneElement(props.element, { onClick: () => newDish() });
  return El;
};

MenuItemForm.defaultProps = {
  hasImageField: true
};

MenuItemForm.propTypes = {
  sectionId: PropTypes.string.isRequired,
  menuId: PropTypes.string.isRequired,
  numOfDishes: PropTypes.number.isRequired,
  element: PropTypes.element.isRequired,
  hasImageField: PropTypes.bool.isRequired,
  dish: PropTypes.object,
};

export default MenuItemForm;
